"use client";
import React from "react";
import { usePathname } from "next/navigation";
import Image from "next/image";
import { getStrapiMedia } from "@aicodepro/web-builder-utils";

interface ContentTextProps {
    title: string;
    text: string;
}

interface Button {
    id: string;
    url: string;
    text: string;
    type: string;
    newTab: boolean;
}

interface Picture {
    data: {
        id: string;
        attributes: {
            url: string;
            name: string;
            alternativeText: string;
        };
    };
}

interface ContentProps {
    data: {
        id: number;
        title: string;
        subtitle: string;
        picture: Picture;
        isReversed: boolean;
        contentTexts: ContentTextProps[];
        button: Button[];
        isLive: boolean;
        useH1Tag?: boolean;
        variant?: "home" | "patent";
        bgColor?: string;
    };
}

export default function Content_Home_Layout4({ data }: ContentProps) {
    const imgUrl = getStrapiMedia(data.picture.data.attributes.url);
    const bgColor = data.bgColor || "#F9F5F0";

    return (
        <section className="relative bg-[#FDF7F1] min-h-screen w-full overflow-hidden px-6 py-16 sm:px-10">
            {/* Background Circles */}
            <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-[#F5E9D9] blur-3xl opacity-30"></div>
            <div className="absolute bottom-0 right-0 w-48 h-48 rounded-full bg-[#F5E9D9] blur-3xl opacity-20"></div>

            <div className="flex flex-col items-center justify-center text-center z-10 relative">
                {/* Image */}
                <div className="w-64 h-64 sm:w-80 sm:h-80 mb-10 relative">
                    <div className="absolute inset-0 rounded-full overflow-hidden border-4 border-[#EADACB] shadow-lg">
                        <Image
                            src={imgUrl || "/placeholder.jpg"}
                            alt={data.picture?.data?.attributes?.alternativeText || "Premium product"}
                            fill
                            className="object-cover"
                            sizes="(max-width: 768px) 100vw, 50vw"
                            priority
                        />
                    </div>
                </div>

                {/* Title */}
                <h1 className="text-[#2A2118] text-4xl sm:text-5xl md:text-6xl   italic font-semibold mb-4">
                    <span className="block text-[#A67C52]  ">Revitalize</span>
                    Your Natural Beauty
                </h1>

                {/* Subtitle */}
                <p className="text-[#5A4A3A]/90 text-base sm:text-lg md:text-xl max-w-2xl mb-6 sm:mb-10 leading-relaxed">
                    Our artisan-crafted herbal blends enhance your hair's natural radiance with 100% organic ingredients.
                </p>

                {/* Feature Points */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl mb-10 text-left">
                    {[
                        "Chemical-free formulations",
                        "Hand-harvested herbs",
                        "Sustainable sourcing",
                        "Proven results",
                    ].map((point, index) => (
                        <div key={index} className="flex items-center gap-3">
                            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-[#EEDFCC]">
                                <svg className="w-3 h-3 text-[#B78D65]" viewBox="0 0 20 20" fill="currentColor">
                                    <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </span>
                            <span className="text-[#5A4A3A] text-base font-medium">{point}</span>
                        </div>
                    ))}
                </div>

                {/* Button */}
                <button className="relative group px-8 py-4 rounded-full bg-[#A67C52] text-white text-lg font-medium tracking-wide hover:shadow-xl transition-shadow">
                    <span className="flex items-center relative z-10">
                        Discover Our Blends
                        <svg
                            className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                    </span>
                    <span className="absolute inset-0 bg-gradient-to-r from-[#B78D65] to-[#A67C52] opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full"></span>
                </button>
            </div>
        </section>
    );
}
